/**
 * Question Bank Page
 * Full-featured page for managing questions
 */

import React, { useEffect } from 'react';
import { QuestionBankBrowser } from '../components/question-bank/QuestionBankBrowser';
import { QuestionEditorDialog } from '../components/question-bank/QuestionEditorDialog';
import { usePopulateQuestionBank, useQuestionBank } from '../hooks/useQuestionBank';
import { Button } from '../components/ui/button';
import { AlertCircle, Database } from 'lucide-react';

interface QuestionBankPageProps {
  firmId: string;
}

export function QuestionBankPage({ firmId }: QuestionBankPageProps) {
  const { data: questionBank, isLoading, error } = useQuestionBank(firmId);
  const populateMutation = usePopulateQuestionBank();

  // Auto-populate if question bank doesn't exist
  useEffect(() => {
    if (!isLoading && !questionBank && !error) {
      populateMutation.mutate(firmId);
    }
  }, [isLoading, questionBank, error, firmId]);

  if (isLoading || populateMutation.isPending) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto"></div>
          <div>
            <h2 className="text-xl font-semibold mb-2">
              {populateMutation.isPending ? 'Setting up Question Bank...' : 'Loading...'}
            </h2>
            <p className="text-muted-foreground">
              {populateMutation.isPending 
                ? 'Populating question bank with default questions'
                : 'Please wait'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-screen p-8">
        <div className="text-center space-y-4 max-w-md">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto">
            <AlertCircle className="w-8 h-8 text-destructive" />
          </div>
          <div>
            <h2 className="text-xl font-semibold mb-2">Error Loading Question Bank</h2>
            <p className="text-muted-foreground mb-4">{error.message}</p>
            {error.message.includes('Failed to fetch') && (
              <div className="mb-4 p-4 bg-muted rounded-lg text-left">
                <p className="text-sm font-semibold mb-2">💡 Backend Not Deployed?</p>
                <p className="text-sm text-muted-foreground mb-2">
                  This error usually means the backend hasn't been deployed yet.
                </p>
                <p className="text-sm text-muted-foreground">
                  Run: <code className="bg-background px-2 py-1 rounded">./deploy-backend-only.sh</code>
                </p>
              </div>
            )}
            <Button onClick={() => populateMutation.mutate(firmId)}>
              <Database className="w-4 h-4 mr-2" />
              Initialize Question Bank
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-background">
      {/* Page Container */}
      <div className="flex-1 overflow-auto p-6">
        <QuestionBankBrowser firmId={firmId} />
      </div>

      {/* Question Editor Dialog */}
      <QuestionEditorDialog firmId={firmId} />
    </div>
  );
}